import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, ZoomIn, ZoomOut } from "lucide-react";
import toast from "react-hot-toast";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { cropImageToFile, type CropArea } from "../utils/cropImage";
import { errorMessage } from "../utils/errorMessage";

type Props = {
  /** The file the merchant picked. Null closes/reset the dialog. */
  file: File | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the square PNG to upload. */
  onCropped: (file: File) => void;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

/**
 * Circular crop step between picking a logo and uploading it. Merchants
 * overwhelmingly upload rectangular brand images, and every surface that
 * renders a logo clips to a circle — without this step the crop is whatever
 * `object-cover` decides, which routinely lops the side off a wordmark.
 */
const LogoCropDialog = ({ file, open, onOpenChange, onCropped }: Props) => {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<CropArea | null>(null);
  const [processing, setProcessing] = useState(false);

  // One blob URL per file, revoked when the file changes or on unmount —
  // react-easy-crop needs a URL, not the File itself.
  useEffect(() => {
    if (!file) {
      setImageSrc("");
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback(
    (_: unknown, croppedAreaPixels: CropArea) => setArea(croppedAreaPixels),
    []
  );

  const handleConfirm = async () => {
    if (!file || !area || processing) return;
    setProcessing(true);
    try {
      onCropped(await cropImageToFile(imageSrc, area, file.name));
      onOpenChange(false);
    } catch (e: any) {
      toast.error(errorMessage(e?.message || "Could not process that image"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md font-manrope">
        <DialogHeader>
          <DialogTitle className="font-sora text-[16px]">
            Crop your logo
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Drag to reposition and zoom to fit. This is exactly how your logo
            will appear to customers.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-[280px] w-full overflow-hidden rounded-xl bg-foreground/90">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <ZoomOut className="size-4 shrink-0 text-muted-foreground" />
          <input
            type="range"
            aria-label="Zoom"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="h-1 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
          />
          <ZoomIn className="size-4 shrink-0 text-muted-foreground" />
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!area || processing}
            className="gap-2 rounded-xl"
          >
            {processing && <Loader2 className="size-4 animate-spin" />}
            Use this logo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogoCropDialog;
